// Components
import {
  FDEInboxToolbar,
  FolderList,
  FolderListPagination,
  RentVerificationList,
  RentVerificationListPagination
} from './components';
import { StyledListView } from './styles';

// Provider
import { HasPropertyProvider } from 'src/context/HasProperty';

// Hooks
import { useSelector } from 'react-redux';
import { useRentalHistoryEnabled } from 'src/hooks';

import { MultiTab } from 'src/components/molecules/MultiTab';
import {
  TabBody,
  TabContent,
  TabHeader,
  TabTitle
} from 'src/components/molecules/MultiTab/MultiTab';
import useRole from 'src/features/auth/hooks/useUserRoles';
import { selectors as fdeInboxSelectors } from 'src/features/fdeInbox/fdeInboxSlice';

import { useMemo } from 'react';
import { useSetFdeInbox } from 'src/features/fdeInbox';
import useHideDocLinks from 'src/features/fdeInbox/hooks/useHideDocLinks';
import { selectors as propertySelectors } from 'src/features/property';
import { FDEInboxIdentityVerificationToolbar } from 'src/pages/FDEInboxPage/components/FDEInboxIdentityVerificationToolbar';
import IdentityVerificationList from 'src/pages/FDEInboxPage/components/IdentityVerificationList';
import IdentityVerificationPagination from 'src/pages/FDEInboxPage/components/IdentityVerificationPagination';
import { fdeInboxTabs } from 'src/ts/enums';
import { FDEInboxRentVerificationToolbar } from './components/FDEInboxRentVerificationToolbar';
import { RentVerificationEmpty } from './components/RentVerification';

const FDEInboxPage = () => {
  const { isLeasingTeam, isAdminOrAccountRepresentative } = useRole();
  const { selectedFdeInboxTab } = useSelector(fdeInboxSelectors);
  const { setSelectedTab } = useSetFdeInbox();

  const hasIdentityVerificationPropertyEnabled = useSelector(
    propertySelectors.hasIdentityVerificationPropertyEnabled.data
  );
  const { hideDocumentsTab } = useHideDocLinks();

  const isIDVViewable = useMemo(() => {
    return hasIdentityVerificationPropertyEnabled &&
      (isAdminOrAccountRepresentative || isLeasingTeam)
      ? true
      : false;
  }, [hasIdentityVerificationPropertyEnabled, isAdminOrAccountRepresentative, isLeasingTeam]);

  const rentalHistoryTabIndex = useMemo(() => {
    let index = 0;
    if (!hideDocumentsTab) index++; // Documents tab
    if (isIDVViewable) index++; // IDV tab
    return index; // Rental history tab position
  }, [hideDocumentsTab, isIDVViewable]);

  const getFilters = useMemo(() => {
    // Users Filter
    if (selectedFdeInboxTab === fdeInboxTabs.DOCUMENTS && !hideDocumentsTab) {
      return <FDEInboxToolbar />;
    }

    if (selectedFdeInboxTab === rentalHistoryTabIndex) {
      return <FDEInboxRentVerificationToolbar />;
    }
    return <FDEInboxIdentityVerificationToolbar />;
  }, [selectedFdeInboxTab, hideDocumentsTab, rentalHistoryTabIndex]);

  const isRentalHistoryEnabled = useRentalHistoryEnabled();

  return (
    <HasPropertyProvider title="FDE Inbox" checkable={isLeasingTeam}>
      <StyledListView title="Verification Inbox" filters={getFilters}>
        <MultiTab
          name="fdeInboxTab"
          selectedIndex={selectedFdeInboxTab}
          hasSpacing
          onSelect={(index) => setSelectedTab(index as number)}
        >
          <TabHeader aria-label="FDE Inbox">
            {hideDocumentsTab ? null : <TabTitle>Income</TabTitle>}
            {isIDVViewable && <TabTitle>Identification</TabTitle>}
            {isRentalHistoryEnabled && <TabTitle>Rent</TabTitle>}
          </TabHeader>
          <TabBody>
            {hideDocumentsTab ? null : (
              <TabContent>
                <FolderList />
                <FolderListPagination />
              </TabContent>
            )}
            {isIDVViewable && (
              <TabContent>
                <IdentityVerificationList />
                <IdentityVerificationPagination />
              </TabContent>
            )}

            <TabContent>
              {isRentalHistoryEnabled ? (
                <>
                  <RentVerificationList />
                  <RentVerificationListPagination />
                </>
              ) : (
                <RentVerificationEmpty />
              )}
            </TabContent>
          </TabBody>
        </MultiTab>
      </StyledListView>
    </HasPropertyProvider>
  );
};

export default FDEInboxPage;
